import * as React from "react";
import { FormControl, Grid, Select, Typography } from "@mui/material";
import { Form, Query } from "../../interfaces";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import {
  getReportPeriod,
  reportPeriodDateFormat,
} from "./FinancialReportingUtilities";

interface Props {
  appState: Query;
  currentQuarterValue: string;
  currentYearValue: string;
  form: Form;
  setForm: (object: any) => void;
  updateReportPeriod: (reportPeriod: string[]) => void;
  updateLatestReportPeriod: (latestReportPeriod: string) => void;
  index?: number;
  inverse?: boolean;
  managepgc?: boolean;
}

interface CurrentReport {
  period: string;
  year: string;
}

export function currentReportMinMax(
  currentReportPeriod: string,
  currentYearValue: string,
  maxReportPeriod: CurrentReport,
  minReportPeriod: CurrentReport,
) {
  return (
    new Date(
      `${currentReportPeriod.substring(0, 2)}/${currentReportPeriod.substring(
        2,
        4,
      )}/${currentYearValue}`,
    ) <
      new Date(
        `${minReportPeriod.period.substring(
          0,
          2,
        )}/${minReportPeriod.period.substring(2, 4)}/${minReportPeriod.year}`,
      ) ||
    new Date(
      `${currentReportPeriod.substring(0, 2)}/${currentReportPeriod.substring(
        2,
        4,
      )}/${currentYearValue}`,
    ) >
      new Date(
        `${maxReportPeriod.period.substring(
          0,
          2,
        )}/${maxReportPeriod.period.substring(2, 4)}/${maxReportPeriod.year}`,
      )
  );
}

export default function ReportingPeriod({
  appState,
  currentQuarterValue,
  currentYearValue,
  form,
  setForm,
  updateReportPeriod,
  updateLatestReportPeriod,
  inverse,
  managepgc,
  index,
}: Props) {
  const currentIndex = index ? index : 0;
  // prevents the api call from running twice
  const [maxReportValues, setMaxReportValues] = React.useState<CurrentReport>({
    year: "",
    period: "",
  });
  const [minReportValues, setMinReportValues] = React.useState<CurrentReport>({
    year: "1992",
    period: "0331",
  });

  React.useEffect(() => {
    if (!appState.ui.latestReportPeriod) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&sort_by=RISDATE&sort_order=DESC&fields=RISDATE`,
        )
          .then(response => {
            if (isMounted) {
              const date = response.data[0].RISDATE;
              updateLatestReportPeriod(date);
              if (!appState.ui.maxRepDate) {
                setMaxReportValues({
                  year: date.substring(0, 4),
                  period: date.substring(4, 6) + date.substring(6, 8),
                });
                if (
                  !JSON.stringify(appState.filters.reportPeriod) ||
                  JSON.stringify(appState.filters.reportPeriod) === '[""]' ||
                  !window.location.search
                ) {
                  updateReportPeriod([date]);
                }
              }
            }
          })
          .catch(() => {
            if (!appState.ui.maxRepDate) {
              const currentDate = getReportPeriod(new Date());
              setMaxReportValues({
                year: currentDate[2],
                period: currentDate[0] + currentDate[1],
              });
              if (!appState.filters.reportPeriod || !window.location.search) {
                updateReportPeriod([
                  currentDate[2] + currentDate[0] + currentDate[1],
                ]);
              }
            }
          });
      })();
      return () => {
        isMounted = false;
      };
    } else if (!appState.ui.maxRepDate) {
      setMaxReportValues({
        year: appState.ui.latestReportPeriod.substring(0, 4),
        period:
          appState.ui.latestReportPeriod.substring(4, 6) +
          appState.ui.latestReportPeriod.substring(6, 8),
      });
    }
  }, [
    appState.filters.reportPeriod,
    appState.ui.latestReportPeriod,
    appState.ui.maxRepDate,
    currentQuarterValue,
    updateLatestReportPeriod,
    updateReportPeriod,
  ]);

  React.useEffect(() => {
    if (appState.ui.maxRepDates && appState.ui.maxRepDates[currentIndex]) {
      setMaxReportValues({
        year: appState.ui.maxRepDates[currentIndex].substring(0, 4),
        period: appState.ui.maxRepDates[currentIndex].substring(4, 8),
      });
    }
  }, [appState.ui.maxRepDates, currentIndex]);

  React.useEffect(() => {
    if (appState.ui.minRepDates && appState.ui.minRepDates.length > 0) {
      const minRepDate =
        appState.ui.minRepDates.length > 1
          ? appState.ui.minRepDates[currentIndex]
          : appState.ui.minRepDates[0];
      setMinReportValues({
        year: minRepDate.substring(0, 4),
        period: minRepDate.substring(4, 8),
      });
    } else {
      setMinReportValues({
        year: "1992",
        period: "0331",
      });
    }
  }, [appState.ui.minRepDates, currentIndex]);

  const handleQuarterChange = (event: any) => {
    //on change, finds the index of the current report and updates the quarter based on that
    const updatedQuater = (form.reportPeriod as string[]).map(
      (report: any, quarterIndex: number) =>
        quarterIndex === currentIndex
          ? `${currentYearValue}${event.target.value}`
          : report,
    );
    setForm({
      ...form,
      reportPeriod: updatedQuater,
    });
  };

  const handleYearChange = (event: any) => {
    const quarterRangeCheck = (value: string) =>
      Number(`${event.target.value}${value}`) <=
        Number(`${maxReportValues.year}${maxReportValues.period}`) &&
      Number(`${event.target.value}${value}`) >=
        Number(`${minReportValues.year}${minReportValues.period}`);

    const dataQuarterRange = ["1231", "0930", "0630", "0331"]
      .map(val => {
        if (quarterRangeCheck(val)) {
          return val;
        }
        return "";
      })
      .filter(val => val !== "")
      .shift();

    //on change, finds the index of the current report and updates the quarter based on that
    const updatedYear = (form.reportPeriod as string[]).map(
      (report: any, yearIndex: number) => {
        return yearIndex === currentIndex
          ? `${event.target.value}${
              quarterRangeCheck(currentQuarterValue)
                ? currentQuarterValue
                : dataQuarterRange
            }`
          : report;
      },
    );

    setForm({
      ...form,
      reportPeriod: updatedYear,
    });
  };

  const yearChoice = () => {
    const years = [];
    const startYear =
      minReportValues.year && Number(minReportValues.year) > 1992
        ? Number(minReportValues.year)
        : 1992;
    const endYear = Number(maxReportValues.year);
    for (let i = startYear; i <= endYear; i++) {
      years.push({ label: i.toString(), value: i.toString() });
    }
    return years;
  };

  const quarterChoice = [
    { value: "0331", label: "1st Quarter" },
    { value: "0630", label: "2nd Quarter" },
    { value: "0930", label: "3rd Quarter" },
    { value: "1231", label: "4th Quarter" },
  ];

  const ReportPeriodText = (
    <Typography
      style={{
        textAlign: "center",
        fontSize: 24,
        fontWeight: "bold",
        marginBottom: ".5rem",
      }}
      color="primary"
      id="report-period-date"
    >
      {reportPeriodDateFormat(currentYearValue + currentQuarterValue)}
    </Typography>
  );
  return (
    <>
      {!managepgc && inverse ? ReportPeriodText : undefined}
      <Grid container spacing={2}>
        <Grid item xs={7}>
          <FormControl variant="outlined" fullWidth size="small" margin="dense">
            <Select
              native
              labelId={`select-filter-quarter-label`}
              id={`select-filter-quarter`}
              value={currentQuarterValue}
              onChange={e => handleQuarterChange(e)}
            >
              {quarterChoice.map(option =>
                !currentReportMinMax(
                  option.value,
                  currentYearValue,
                  maxReportValues,
                  minReportValues,
                ) ? (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ) : (
                  undefined
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={5}>
          <FormControl variant="outlined" fullWidth size="small" margin="dense">
            <Select
              native
              labelId={`select-filter-year-label`}
              id={`select-filter-year`}
              value={currentYearValue}
              onChange={e => handleYearChange(e)}
            >
              {yearChoice().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {!managepgc && !inverse ? ReportPeriodText : undefined}
    </>
  );
}
